import { useState, useEffect, useCallback, useRef } from "react";
import { ShieldCheck, AlertTriangle, CheckCircle, ChevronsUpDown } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// SUPABASE CONFIG
// ═══════════════════════════════════════════════════════════════════════════════
const SUPABASE_URL = "https://pkermjufxfswjbhmwfpk.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBrZXJtanVmeGZzd2piaG13ZnBrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM5Mjg4OTAsImV4cCI6MjA4OTUwNDg5MH0.hvKo0u4CDqqV-G-AcdSwmJVbd4MLUxo5aOKJHFCyEV8";

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
const C = {
  bg: "#0B0F1A", surface: "#111827", surfaceAlt: "#1a2235", border: "#1f2d45",
  accent: "#16a34a", accentDim: "#15803d", accentGlow: "rgba(22,163,74,0.15)",
  text: "#F1F5F9", textMuted: "#64748B", textSub: "#94A3B8",
  green: "#10B981", greenBg: "rgba(16,185,129,0.12)",
  amber: "#F59E0B", amberBg: "rgba(245,158,11,0.12)",
  red: "#EF4444", redBg: "rgba(239,68,68,0.12)",
  blue: "#3B82F6", blueBg: "rgba(59,130,246,0.12)",
};

// ── SHIMMER ───────────────────────────────────────────────────────────────────
const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position:  400px 0; }
  }
`;

// ── UTILITIES ─────────────────────────────────────────────────────────────────
const vesStatus = (dateStr, motStatus) => {
  if (!dateStr) return { color: C.textMuted, icon: null, label: "—", raw: "—", statusLabel: motStatus || "—" };
  const date = new Date(dateStr);
  const now  = new Date();
  const diffDays = Math.floor((date - now) / (1000 * 60 * 60 * 24));
  const label = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  if (diffDays < 0)   return { color: C.red,   label, raw: dateStr, statusLabel: motStatus || "Expired" };
  if (diffDays <= 30) return { color: C.amber, label, raw: dateStr, statusLabel: "Valid" };
  return                     { color: C.green, label, raw: dateStr, statusLabel: "Valid" };
};

const fmtDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// ── SHARED COMPONENTS ─────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, ...style }}>{children}</div>
);

const Btn = ({ children, onClick, variant = "primary", small, disabled, style = {} }) => {
  const v = {
    primary:   { background: disabled ? C.surfaceAlt : C.accent, color: disabled ? C.textMuted : "#fff", border: "none" },
    secondary: { background: C.surfaceAlt, color: C.textSub,  border: `1px solid ${C.border}` },
    danger:    { background: C.redBg,      color: C.red,       border: `1px solid ${C.red}33`  },
    ghost:     { background: "transparent",color: C.textSub,  border: `1px solid ${C.border}` },
  };
  return (
    <button disabled={disabled} style={{ borderRadius: 8, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", transition: "opacity 0.15s", fontSize: small ? 12 : 14, padding: small ? "6px 12px" : "9px 18px", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit", ...v[variant], ...style }}
      onClick={onClick}
      onMouseOver={e => !disabled && (e.currentTarget.style.opacity = "0.8")}
      onMouseOut={e => (e.currentTarget.style.opacity = "1")}>
      {children}
    </button>
  );
};

const Input = ({ label, type = "text", value, onChange, placeholder, autoComplete }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</label>}
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} autoComplete={autoComplete}
      style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
  </div>
);


// ═══════════════════════════════════════════════════════════════════════════════
// DRIVER PORTAL
// ═══════════════════════════════════════════════════════════════════════════════
const DriverPortalApp = ({ supabaseUrl, supabaseKey }) => {
  const [portalView, setPortalView]         = useState("login"); // "login" | "forgot" | "reset-password" | "select-company" | "dashboard"
  const [portalUser, setPortalUser]         = useState(null);
  const [portalEmail, setPortalEmail]       = useState("");
  const [portalPassword, setPortalPassword] = useState("");
  const [portalLoginError, setPortalLoginError] = useState("");
  const [portalLoggingIn, setPortalLoggingIn]   = useState(false);
  const [forgotSent, setForgotSent]         = useState(false);
  const [forgotError, setForgotError]       = useState("");
  const [forgotSending, setForgotSending]   = useState(false);
  const [portalLicence, setPortalLicence]   = useState(null);
  const [portalVehicles, setPortalVehicles] = useState([]);
  const [portalVes, setPortalVes]           = useState({});
  const [portalDataLoading, setPortalDataLoading] = useState(false);
  const [vehicleIndex, setVehicleIndex]     = useState(0);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const carouselContainerRef = useRef(null);
  const [showDeleteVehicle, setShowDeleteVehicle]   = useState(false);
  const [deletingVehicle, setDeletingVehicle]       = useState(false);
  const [showAddVehicle, setShowAddVehicle]         = useState(false);
  const [showResubmitInsurance, setShowResubmitInsurance] = useState(false);
  const [resubmitVehicle, setResubmitVehicle]             = useState(null);
  const [resubmitFile, setResubmitFile]                   = useState(null);
  const [resubmitFileName, setResubmitFileName]           = useState("");
  const [resubmitSubmitting, setResubmitSubmitting]       = useState(false);
  const [resubmitDone, setResubmitDone]                   = useState(false);
  const [resubmitError, setResubmitError]                 = useState("");
  const [showInsuranceDetail, setShowInsuranceDetail]     = useState(false);
  const [insuranceDetailVehicle, setInsuranceDetailVehicle] = useState(null);
  const [addVehicleReg, setAddVehicleReg]           = useState("");
  const [addVehicleFile, setAddVehicleFile]         = useState(null);
  const [addVehicleFileName, setAddVehicleFileName] = useState("");
  const [addVehicleError, setAddVehicleError]       = useState("");
  const [addVehicleSubmitting, setAddVehicleSubmitting] = useState(false);
  const [addVehicleDone, setAddVehicleDone]         = useState(false);
  const [showGdprModal, setShowGdprModal]   = useState(false);
  const [gdprConfirmText, setGdprConfirmText] = useState("");
  const [gdprDeleting, setGdprDeleting]     = useState(false);
  const [gdprDeleted, setGdprDeleted]       = useState(false);
  const [resetNewPassword, setResetNewPassword]     = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetSubmitting, setResetSubmitting]       = useState(false);
  const [resetError, setResetError]                 = useState("");
  const [resetDone, setResetDone]                   = useState(false);
  const resetTokenRef = useRef(null);
  const [companyChoices, setCompanyChoices]         = useState([]);
  const [portalCompanyName, setPortalCompanyName]   = useState("");
  const [showCompanySwitcher, setShowCompanySwitcher] = useState(false);
  const fetchPortalData = useCallback(async () => {
    if (!portalUser) return;
    setPortalDataLoading(true);
    const headers = { apikey: supabaseKey, Authorization: `Bearer ${portalUser.accessToken}` };
    try {
      const [licRes, gfRes] = await Promise.all([
        fetch(`${supabaseUrl}/rest/v1/licence_summaries?driver_id=eq.${portalUser.id}&order=extracted_at.desc&limit=1`, { headers }),
        fetch(`${supabaseUrl}/rest/v1/grey_fleet_checks?driver_id=eq.${portalUser.id}&order=is_primary.desc,created_at.desc`, { headers }),
      ]);
      const [licData, gfData] = await Promise.all([licRes.json(), gfRes.json()]);
      const lic = Array.isArray(licData) && licData.length ? licData[0] : null;
      const vehicles = Array.isArray(gfData) ? gfData : [];
      setPortalLicence(lic);
      setPortalVehicles(vehicles);
      const regs = vehicles.map(v => v.reg).filter(Boolean).map(r => r.replace(/\s+/g, "").toUpperCase());
      if (regs.length > 0) {
        const vesRes = await fetch(
          `${supabaseUrl}/rest/v1/vehicle_ves_data?registration=in.(${regs.join(",")})&select=*`,
          { headers: { apikey: supabaseKey, Authorization: `Bearer ${supabaseKey}` } }
        );
        const vesRows = await vesRes.json();
const vesMap = {};
        if (Array.isArray(vesRows)) vesRows.forEach(row => { vesMap[row.registration] = row; });
        setPortalVes(vesMap);
      }
    } catch (err) {
      console.error("Portal data fetch error:", err);
    }
    setPortalDataLoading(false);
  }, [portalUser, supabaseUrl, supabaseKey]);

  useEffect(() => {
    if (portalView !== "dashboard" || !portalUser) return;
    fetchPortalData();
  }, [fetchPortalData, portalUser, portalView]);

  useEffect(() => {
    console.log("[reset-link] hash:", window.location.hash);
    console.log("[reset-link] search:", window.location.search);
    const hash = window.location.hash;
    if (hash && hash.includes("access_token") && hash.includes("type=recovery")) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get("access_token");
      if (accessToken) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        resetTokenRef.current = accessToken;
        setPortalView("reset-password");
      }
    }
  }, []);

  useEffect(() => {
    const el = carouselContainerRef.current;
    if (!el) return;
    const prevent = (e) => {
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
      if (deltaX > deltaY) e.preventDefault();
    };
    el.addEventListener("touchmove", prevent, { passive: false });
    return () => el.removeEventListener("touchmove", prevent);
  }, []);


  const handlePortalLogin = async () => {
    setPortalLoginError("");
    setPortalLoggingIn(true);
    try {
      const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: supabaseKey },
        body: JSON.stringify({ email: portalEmail, password: portalPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error_description || "Invalid email or password");

      const driverRes = await fetch(
        `${supabaseUrl}/rest/v1/drivers?portal_user_id=eq.${data.user.id}&select=*,companies(name)`,
        { headers: { apikey: supabaseKey, Authorization: `Bearer ${data.access_token}` } }
      );
      const drivers = await driverRes.json();
      console.log("[handlePortalLogin] auth user id:", data.user.id, "drivers response:", JSON.stringify(drivers));
      if (!drivers?.length) throw new Error("No driver record found for this account");

      const choices = drivers.map(d => ({
        ...d,
        accessToken: data.access_token,
        companyName: d.companies?.name || "Unknown Company",
      }));
      setCompanyChoices(choices);
      setPortalCompanyName(choices[0].companyName);
      setPortalUser(choices[0]);
      setPortalView("dashboard");
    } catch (err) {
      setPortalLoginError(err.message || "Sign in failed");
    }
    setPortalLoggingIn(false);
  };

  const handleForgotPassword = async () => {
    setForgotError("");
    setForgotSending(true);
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/send-password-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": supabaseKey,
          "Authorization": `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({ email: portalEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send reset email.");
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.message || "Something went wrong. Please try again.");
    }
    setForgotSending(false);
  };

  const handleDeleteVehicle = async () => {
    const vehicle = portalVehicles[vehicleIndex];
    if (!vehicle || vehicle.isPrimary) return;
    setDeletingVehicle(true);
    try {
      await fetch(
        `${supabaseUrl}/rest/v1/grey_fleet_checks?driver_id=eq.${portalUser.id}&reg=eq.${vehicle.reg.replace(/\s+/g, "").toUpperCase()}`,
        {
          method: "DELETE",
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${portalUser.accessToken}`,
            Prefer: "return=minimal",
          },
        }
      );
      setShowDeleteVehicle(false);
      setVehicleIndex(0);
      await fetchPortalData();
    } catch (err) {
      console.error("Delete vehicle error:", err);
    }
    setDeletingVehicle(false);
  };

  const handleAddVehicle = async () => {
    setAddVehicleError("");
    const reg = addVehicleReg.replace(/\s+/g, "").toUpperCase();
    if (!reg) { setAddVehicleError("Please enter your vehicle registration"); return; }
    if (!addVehicleFile) { setAddVehicleError("Please upload your insurance certificate"); return; }

    setAddVehicleSubmitting(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(addVehicleFile);
      });

      const res = await fetch(`${supabaseUrl}/functions/v1/check-insurance-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: supabaseKey,
          Authorization: `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          insurance_pdf_base64: base64,
          vehicle_registration: reg,
          driver_id:  portalUser.id,
          company_id: portalUser.company_id,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Submission failed");

      setAddVehicleDone(true);
      setTimeout(() => {
        setShowAddVehicle(false);
        setAddVehicleReg("");
        setAddVehicleFile(null);
        setAddVehicleFileName("");
        setAddVehicleDone(false);
        fetchPortalData();
      }, 2000);

    } catch (err) {
      console.error("handleAddVehicle error:", err);
      setAddVehicleError(err.message || "Something went wrong. Please try again.");
    }
    setAddVehicleSubmitting(false);
  };

  const handleResubmitInsurance = async () => {
    setResubmitError("");
    if (!resubmitFile) { setResubmitError("Please upload your new insurance certificate"); return; }
    const reg = resubmitVehicle?.reg?.replace(/\s+/g, "").toUpperCase();
    if (!reg) return;

    setResubmitSubmitting(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result.split(",")[1]);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(resubmitFile);
      });

      const res = await fetch(`${supabaseUrl}/functions/v1/check-insurance-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: supabaseKey, Authorization: `Bearer ${supabaseKey}` },
        body: JSON.stringify({
          insurance_pdf_base64: base64,
          vehicle_registration: reg,
          driver_id:  portalUser.id,
          company_id: portalUser.company_id,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Submission failed");

      setResubmitDone(true);
      setTimeout(() => {
        setShowResubmitInsurance(false);
        setResubmitVehicle(null);
        setResubmitFile(null);
        setResubmitFileName("");
        setResubmitDone(false);
        fetchPortalData();
      }, 2000);
    } catch (err) {
      setResubmitError(err.message || "Something went wrong. Please try again.");
    }
    setResubmitSubmitting(false);
  };

  const handleGdprDelete = async () => {
    setGdprDeleting(true);
    try {
      await fetch(`${supabaseUrl}/functions/v1/delete-portal-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: supabaseKey,
          Authorization: `Bearer ${portalUser.accessToken}`,
        },
        body: JSON.stringify({ driver_id: portalUser.id }),
      });
    } catch (err) {
      console.error("delete-portal-account error:", err);
    }
    try {
      await fetch(`${supabaseUrl}/rest/v1/removal_requests`, {
        method: "POST",
        headers: {
          apikey: supabaseKey,
          Authorization: `Bearer ${portalUser.accessToken}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          driver_id:       portalUser.id,
          acknowledged_at: new Date().toISOString(),
          deleted_at:      new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.error("removal_requests insert error:", err);
    }
    setGdprDeleting(false);
    setGdprDeleted(true);
    setShowGdprModal(false);
  };

  const Logo = () => (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{ width: 36, height: 36, background: C.accent, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ShieldCheck size={20} color="#fff" strokeWidth={2} />
      </div>
      <span style={{ color: C.text, fontWeight: 800, fontSize: 20 }}>FleetCustodian</span>
    </div>
  );

  if (portalView === "login") return (
    <div style={{ minHeight: "-webkit-fill-available", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans','Segoe UI',sans-serif", overscrollBehavior: "none", WebkitOverflowScrolling: "touch" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Logo />
          <div style={{ color: C.text, fontWeight: 700, fontSize: 24, marginBottom: 6 }}>Driver Compliance Portal</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>Sign in to view your compliance record</div>
        </div>
        <Card>
          <Input label="Email Address" type="email" value={portalEmail} onChange={e => setPortalEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
          <Input label="Password" type="password" value={portalPassword} onChange={e => setPortalPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
          {portalLoginError && (
            <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={14} color={C.red} /> {portalLoginError}
            </div>
          )}
          <Btn onClick={handlePortalLogin} disabled={portalLoggingIn} style={{ width: "100%", padding: "12px 0", fontSize: 15, marginTop: 4, justifyContent: "center" }}>
            {portalLoggingIn ? "Signing in…" : "Sign In"}
          </Btn>
          <div style={{ textAlign: "center", marginTop: 16, color: C.textMuted, fontSize: 13 }}>
            <span onClick={() => { setPortalLoginError(""); setPortalView("forgot"); }} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>Forgot your password?</span>
          </div>
        </Card>
      </div>
    </div>
  );

  if (portalView === "reset-password") {
    const handleResetSubmit = async () => {
      setResetError("");
      if (!resetNewPassword) { setResetError("Please enter a new password."); return; }
      if (resetNewPassword.length < 6) { setResetError("Password must be at least 6 characters."); return; }
      if (resetNewPassword !== resetConfirmPassword) { setResetError("Passwords do not match."); return; }
      setResetSubmitting(true);
      try {
        const res = await fetch(`${supabaseUrl}/auth/v1/user`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "apikey": supabaseKey,
            "Authorization": `Bearer ${resetTokenRef.current}`,
          },
          body: JSON.stringify({ password: resetNewPassword }),
        });
        if (!res.ok) {
          const err = await res.text();
          throw new Error(err || "Failed to update password");
        }
        setResetDone(true);
        setTimeout(() => {
          setResetNewPassword("");
          setResetConfirmPassword("");
          setResetDone(false);
          resetTokenRef.current = null;
          setPortalView("login");
        }, 2500);
      } catch (err) {
        setResetError(err.message || "Something went wrong. Please try again.");
      }
      setResetSubmitting(false);
    };

    return (
      <div style={{ minHeight: "-webkit-fill-available", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans','Segoe UI',sans-serif", overscrollBehavior: "none", WebkitOverflowScrolling: "touch" }}>
        <div style={{ width: "100%", maxWidth: 400 }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <Logo />
            <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>Set a new password</div>
            <div style={{ color: C.textMuted, fontSize: 14 }}>Choose a new password for your account</div>
          </div>
          <Card>
            {resetDone ? (
              <div style={{ textAlign: "center", padding: "8px 0 12px" }}>
                <CheckCircle size={40} color={C.green} style={{ margin: "0 auto 12px" }} />
                <div style={{ color: C.green, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Password updated</div>
                <div style={{ color: C.textMuted, fontSize: 14 }}>Redirecting you to sign in…</div>
              </div>
            ) : (
              <>
                <Input label="New Password" type="password" value={resetNewPassword} onChange={e => setResetNewPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
                <Input label="Confirm Password" type="password" value={resetConfirmPassword} onChange={e => setResetConfirmPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
                {resetError && (
                  <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                    <AlertTriangle size={14} color={C.red} /> {resetError}
                  </div>
                )}
                <Btn onClick={handleResetSubmit} disabled={resetSubmitting} style={{ width: "100%", padding: "12px 0", fontSize: 15, marginTop: 4, justifyContent: "center" }}>
                  {resetSubmitting ? "Updating…" : "Update Password"}
                </Btn>
              </>
            )}
          </Card>
        </div>
      </div>
    );
  }

  if (portalView === "forgot") return (
    <div style={{ minHeight: "-webkit-fill-available", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans','Segoe UI',sans-serif", overscrollBehavior: "none", WebkitOverflowScrolling: "touch" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Logo />
          <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>Reset your password</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>Enter your email and we'll send you a reset link</div>
        </div>
        <Card>
          {forgotSent ? (
            <>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <CheckCircle size={40} color={C.green} style={{ margin: "0 auto 12px" }} />
                <div style={{ color: C.green, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Check your email</div>
                <div style={{ color: C.textMuted, fontSize: 14 }}>We've sent a reset link to <strong style={{ color: C.text }}>{portalEmail}</strong></div>
              </div>
            </>
          ) : (
            <>
              <Input label="Email Address" type="email" value={portalEmail} onChange={e => setPortalEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
              {forgotError && (
                <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={14} color={C.red} /> {forgotError}
                </div>
              )}
              <Btn onClick={handleForgotPassword} disabled={forgotSending} style={{ width: "100%", padding: "12px 0", fontSize: 15, marginTop: 4, justifyContent: "center" }}>
                {forgotSending ? "Sending…" : "Send reset link"}
              </Btn>
            </>
          )}
          <div style={{ textAlign: "center", marginTop: 16, color: C.textMuted, fontSize: 13 }}>
            <span onClick={() => { setForgotSent(false); setForgotError(""); setPortalView("login"); }} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>← Back to sign in</span>
          </div>
        </Card>
      </div>
    </div>
  );

  if (portalView === "select-company") return (
    <div style={{ minHeight: "-webkit-fill-available", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans','Segoe UI',sans-serif", overscrollBehavior: "none", WebkitOverflowScrolling: "touch" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Logo />
          <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>Select your company</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>You're registered with multiple companies</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {companyChoices.map(driver => (
            <button
              key={driver.id}
              onClick={() => { setPortalCompanyName(driver.companyName); setPortalUser(driver); setPortalView("dashboard"); }}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 20px", cursor: "pointer", textAlign: "left", fontFamily: "inherit", width: "100%", transition: "border-color 0.15s" }}
              onMouseOver={e => e.currentTarget.style.borderColor = C.accent}
              onMouseOut={e => e.currentTarget.style.borderColor = C.border}
            >
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>{driver.companyName}</div>
              <div style={{ color: C.textMuted, fontSize: 13, marginTop: 4 }}>{driver.name}</div>
            </button>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 20, color: C.textMuted, fontSize: 13 }}>
          <span onClick={() => { setCompanyChoices([]); setPortalView("login"); }} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>← Back to sign in</span>
        </div>
      </div>
    </div>
  );

  if (portalView === "dashboard") {
    const licenceStatusCfg = {
      Valid:   { color: C.green, bg: C.greenBg, label: "Valid" },
      Banned:  { color: C.red,   bg: C.redBg,   label: "Banned" },
      Expired: { color: C.amber, bg: C.amberBg, label: "Expired" },
    }[portalUser?.licence_status] || { color: C.textMuted, bg: C.surfaceAlt, label: portalUser?.licence_status || "Unknown" };

    const SkeletonLine = ({ w = "100%", h = 14, mb = 8 }) => (
      <div style={{ width: w, height: h, borderRadius: 6, marginBottom: mb, background: `linear-gradient(90deg, ${C.surfaceAlt} 25%, ${C.border} 50%, ${C.surfaceAlt} 75%)`, backgroundSize: "400px 100%", animation: "shimmer 1.4s infinite linear" }} />
    );

    const DashField = ({ label, value, valueColor }) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7 }}>{label}</div>
        <div style={{ color: valueColor || C.text, fontSize: 14, fontWeight: 600 }}>{value || "—"}</div>
      </div>
    );

    const slideWidth = portalVehicles.length > 0 ? 100 / portalVehicles.length : 100;

    return (
      <div style={{ minHeight: "-webkit-fill-available", background: C.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif", overscrollBehavior: "none", WebkitOverflowScrolling: "touch" }}>
        <style>{shimmer}</style>

        {/* Company switcher modal */}
        {showCompanySwitcher && (
          <div
            onClick={() => setShowCompanySwitcher(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, width: "100%", maxWidth: 400 }}
            >
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Switch company</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {companyChoices.map(driver => (
                  <button
                    key={driver.id}
                    onClick={() => { setPortalUser(driver); setPortalCompanyName(driver.companyName); setShowCompanySwitcher(false); }}
                    style={{ background: driver.id === portalUser?.id ? C.accentGlow : C.surfaceAlt, border: `1px solid ${driver.id === portalUser?.id ? C.accent : C.border}`, borderRadius: 10, padding: "12px 16px", cursor: "pointer", textAlign: "left", fontFamily: "inherit", width: "100%" }}
                  >
                    <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{driver.companyName}</div>
                    <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{driver.name}</div>
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <span onClick={() => setShowCompanySwitcher(false)} style={{ color: C.textMuted, fontSize: 13, cursor: "pointer" }}>Cancel</span>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 20px" }}>
          <div style={{ maxWidth: 480, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 30, height: 30, background: C.accent, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ShieldCheck size={16} color="#fff" strokeWidth={2.2} />
                </div>
                <span style={{ color: C.text, fontWeight: 800, fontSize: 17 }}>FleetCustodian</span>
              </div>
              <Btn variant="secondary" small onClick={() => { setPortalUser(null); setPortalPassword(""); setPortalLoginError(""); setPortalView("login"); }}>
                Sign Out
              </Btn>
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 18 }}>{portalUser?.name || "Driver"}</div>
              <div style={{ color: C.textMuted, fontSize: 13 }}>{portalUser?.email || ""}</div>
              {portalCompanyName && (
                <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                  Compliance record for:{" "}
                  {companyChoices.length > 1 ? (
                    <span
                      onClick={() => setShowCompanySwitcher(true)}
                      style={{ color: C.accent, cursor: "pointer", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}
                    >
                      {portalCompanyName}
                      <ChevronsUpDown size={11} />
                    </span>
                  ) : (
                    <span style={{ color: C.textSub }}>{portalCompanyName}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "20px 16px 40px" }}>

          {/* Licence Card */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Driving Licence</div>
              {!portalDataLoading && (
                <span style={{ background: licenceStatusCfg.bg, color: licenceStatusCfg.color, border: `1px solid ${licenceStatusCfg.color}33`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700 }}>
                  {licenceStatusCfg.label}
                </span>
              )}
            </div>

            {portalDataLoading ? (
              <>
                <SkeletonLine w="60%" h={12} mb={16} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {[1,2,3,4,5].map(i => <SkeletonLine key={i} w="80%" h={28} mb={0} />)}
                </div>
              </>
            ) : portalLicence ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px", marginBottom: 16 }}>
                  {(() => { const p = portalUser?.points ?? portalLicence?.points; const n = Number(p); const isZero = p !== null && p !== undefined && n === 0; return <DashField label="Points" value={p === null || p === undefined ? "—" : String(p)} valueColor={isZero ? C.green : undefined} />; })()}
                  {(() => { const e = portalUser?.endorsements ?? portalLicence?.endorsements; const isBlank = e === null || e === undefined || e === "" || e === "—" || Number(e) === 0; return <DashField label="Endorsements" value={isBlank ? "0" : String(e)} valueColor={isBlank ? C.green : undefined} />; })()}
                  <DashField label="Licence Valid To" value={fmtDate(portalLicence.licence_valid_to)} valueColor={portalLicence.licence_valid_to ? vesStatus(portalLicence.licence_valid_to).color : undefined} />
                  <DashField label="Re-check Due"     value={fmtDate(portalUser?.recheck_due)} valueColor={portalUser?.recheck_due && new Date(portalUser.recheck_due) < new Date() ? C.red : undefined} />
                </div>
                {Array.isArray(portalLicence.categories) && portalLicence.categories.length > 0 && (
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 4 }}>
                    <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 8 }}>Licence categories</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {portalLicence.categories.map((c, i) => (
                        <span key={i} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.accent, borderRadius: 6, padding: "3px 9px", fontSize: 12, fontWeight: 700, letterSpacing: 0.3 }}>
                          {c.category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: C.textMuted, fontSize: 14, textAlign: "center", padding: "16px 0" }}>No licence data on record yet</div>
            )}
          </div>

          {/* Vehicle Carousel */}
          {portalDataLoading ? (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Your Vehicle</div>
              <SkeletonLine w="40%" h={36} mb={16} />
              <SkeletonLine w="55%" h={12} mb={12} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[1,2,3,4].map(i => <SkeletonLine key={i} w="80%" h={28} mb={0} />)}
              </div>
            </div>
          ) : portalVehicles.length === 0 ? (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Your Vehicle</div>
              <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 16 }}>No vehicle on record — add your vehicle to track MOT, tax and insurance expiry.</div>
              <Btn variant="secondary" onClick={() => { setAddVehicleError(""); setAddVehicleReg(""); setAddVehicleFile(null); setAddVehicleFileName(""); setAddVehicleDone(false); setShowAddVehicle(true); }} style={{ width: "100%" }}>+ Add a vehicle</Btn>
            </div>
          ) : (
            <>
              {/* State-driven carousel */}
              <div
                ref={carouselContainerRef}
                style={{ overflow: "hidden", width: "100%", marginBottom: 8, borderRadius: 14 }}
                onTouchStart={(e) => { touchStartRef.current.x = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                  const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
                  if (Math.abs(deltaX) > 50) {
                    if (deltaX < 0) setVehicleIndex(i => Math.min(portalVehicles.length - 1, i + 1));
                    else setVehicleIndex(i => Math.max(0, i - 1));
                  }
                }}
              >
                <div style={{
                  display: "flex",
                  width: `${portalVehicles.length * 100}%`,
                  transform: `translateX(-${vehicleIndex * slideWidth}%)`,
                  transition: "transform 0.35s ease",
                }}>
                  {portalVehicles.map((gf, i) => {
                    const normReg = gf?.reg ? gf.reg.replace(/\s+/g, "").toUpperCase() : null;
                    const ves = normReg ? portalVes[normReg] : null;
                    const motSt = vesStatus(ves?.mot_expiry, ves?.mot_status);
                    const taxSt = vesStatus(ves?.tax_expiry, ves?.tax_status);
                    const insExpired = gf?.policy_expiry && new Date(gf.policy_expiry) < new Date();
                    const insColor   = insExpired ? C.red : (gf?.ai_outcome === "Green" ? C.green : C.amber);
                    const insLabel   = insExpired ? "Insurance Expired" : (gf?.ai_outcome === "Green" ? "Confirmed" : "Action Required");
                    const insCfg     = gf?.ai_outcome || insExpired ? { color: insColor, bg: insExpired ? C.redBg : (gf?.ai_outcome === "Green" ? C.greenBg : C.amberBg), label: insLabel } : null;
                    return (
                      <div key={i} style={{ width: `${slideWidth}%`, flexShrink: 0, boxSizing: "border-box" }}>
                        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                          {/* Card header */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                            <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Your Vehicle</div>
                            {!gf.isPrimary && (
                              <span onClick={() => setShowDeleteVehicle(true)} style={{ fontSize: 12, color: C.red, cursor: "pointer" }}>Remove</span>
                            )}
                          </div>
                          {/* UK plate */}
                          {gf?.reg && (
                            <div style={{ display: "inline-flex", alignItems: "center", borderRadius: 6, overflow: "hidden", border: "2px solid #1a1a1a", marginBottom: 16, boxShadow: "0 2px 6px rgba(0,0,0,0.4)" }}>
                              <div style={{ background: "#003399", width: 22, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "6px 3px", gap: 2 }}>
                                <div style={{ color: "#FFD700", fontSize: 7, fontWeight: 800, letterSpacing: 0.5, lineHeight: 1 }}>GB</div>
                                <div style={{ color: "#FFD700", fontSize: 14, lineHeight: 1 }}>★</div>
                              </div>
                              <div style={{ background: "#F5C518", padding: "6px 14px" }}>
                                <span style={{ color: "#000", fontWeight: 800, fontSize: 22, fontFamily: "'UKNumberPlate','Arial Black','Arial',sans-serif", letterSpacing: 3 }}>{gf.reg}</span>
                              </div>
                            </div>
                          )}
                          {/* Insurance status — tappable */}
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>INSURANCE STATUS</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              {insCfg ? (
                                <span
                                  onClick={() => { setInsuranceDetailVehicle(gf); setShowInsuranceDetail(true); }}
                                  style={{ background: insCfg.bg, color: insCfg.color, border: `1px solid ${insCfg.color}44`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}
                                >
                                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: insCfg.color }} />
                                  {insLabel}
                                  <span style={{ fontSize: 10, opacity: 0.7 }}>ⓘ</span>
                                </span>
                              ) : (
                                <span style={{ color: C.textMuted, fontSize: 13 }}>Not checked</span>
                              )}
                              <button
                                onClick={() => { setResubmitVehicle(gf); setResubmitFile(null); setResubmitFileName(""); setResubmitError(""); setResubmitDone(false); setShowResubmitInsurance(true); }}
                                style={{ background: "transparent", border: `1px solid ${C.accent}`, borderRadius: 20, padding: "4px 12px", color: C.accent, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                              >
                                Upload new certificate
                              </button>
                            </div>
                          </div>
                          {/* Fields */}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                            <DashField label="Policy Expiry" value={fmtDate(gf?.policy_expiry)} valueColor={gf?.policy_expiry ? vesStatus(gf.policy_expiry).color : undefined} />
                            <DashField label="MOT Expiry"    value={ves ? motSt.label : "—"} valueColor={ves ? motSt.color : undefined} />
                            <DashField label="MOT Status"    value={ves ? motSt.statusLabel : "—"} />
                            <DashField label="Tax Expiry"    value={ves ? taxSt.label : "—"} valueColor={ves ? taxSt.color : undefined} />
                            <DashField label="Tax Status"    value={ves?.tax_status || "—"} />
                            {ves?.make && <DashField label="Vehicle" value={`${ves.make}${ves.model ? ' ' + ves.model : ''}`} />}
                            {ves?.colour && <DashField label="Colour" value={ves.colour} />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dot indicators */}
              <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 8, marginBottom: 12 }}>
                {portalVehicles.map((_, i) => (
                  <div
                    key={i}
                    onClick={() => setVehicleIndex(i)}
                    style={{ width: i === vehicleIndex ? 20 : 8, height: 8, borderRadius: 4, background: i === vehicleIndex ? C.accent : C.surfaceAlt, transition: "all 0.3s", cursor: "pointer" }}
                  />
                ))}
              </div>

              {/* Add vehicle button */}
              <div style={{ marginBottom: 16 }}>
                <Btn variant="secondary" onClick={() => { setAddVehicleError(""); setAddVehicleReg(""); setAddVehicleFile(null); setAddVehicleFileName(""); setAddVehicleDone(false); setShowAddVehicle(true); }} style={{ width: "100%" }}>+ Add a vehicle</Btn>
              </div>
            </>
          )}

          {/* Footer */}
          <div style={{ textAlign: "center", color: C.textMuted, fontSize: 12, lineHeight: 1.6, marginTop: 8 }}>
            Data provided by FleetCustodian.<br />For queries, contact your employer.<br />
            <span
              onClick={() => { setGdprConfirmText(""); setShowGdprModal(true); }}
              style={{ color: C.accent, cursor: "pointer", fontWeight: 600, marginTop: 6, display: "inline-block" }}
            >
              Request data erasure (GDPR)
            </span>
          </div>
        </div>

        {/* Remove vehicle confirmation modal */}
        {showDeleteVehicle && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 400 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Remove vehicle</div>
              <div style={{ color: C.textSub, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                Are you sure you want to remove <strong style={{ color: C.text }}>{portalVehicles[vehicleIndex]?.reg}</strong> from your compliance record? This cannot be undone.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setShowDeleteVehicle(false)}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteVehicle}
                  disabled={deletingVehicle}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: C.red, color: "#fff", fontSize: 14, fontWeight: 600, cursor: deletingVehicle ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: deletingVehicle ? 0.7 : 1 }}
                >
                  {deletingVehicle ? "Removing…" : "Remove vehicle"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add vehicle modal */}
        {showAddVehicle && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 400 }}>
              {addVehicleDone ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <CheckCircle size={44} color={C.green} style={{ margin: "0 auto 14px" }} />
                  <div style={{ color: C.green, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Vehicle added</div>
                  <div style={{ color: C.textMuted, fontSize: 14 }}>Your insurance certificate is being reviewed.</div>
                </div>
              ) : (
                <>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 20 }}>Add a vehicle</div>

                  {/* Registration input */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 6 }}>Vehicle Registration</div>
                    <input
                      value={addVehicleReg}
                      onChange={e => setAddVehicleReg(e.target.value.toUpperCase())}
                      placeholder="e.g. AB12 CDE"
                      style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 15, fontWeight: 700, letterSpacing: 2, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                    />
                  </div>

                  {/* PDF upload dropzone */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 6 }}>Insurance Certificate (PDF)</div>
                    <label style={{ display: "block", cursor: "pointer" }}>
                      <input
                        type="file"
                        accept="application/pdf"
                        style={{ display: "none" }}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f && f.type === "application/pdf") {
                            setAddVehicleFile(f);
                            setAddVehicleFileName(f.name);
                          }
                        }}
                      />
                      <div style={{ border: `2px dashed ${addVehicleFile ? C.green : C.border}`, borderRadius: 10, padding: "18px 12px", textAlign: "center", background: addVehicleFile ? C.greenBg : C.surfaceAlt, transition: "border-color 0.2s, background 0.2s" }}>
                        {addVehicleFile ? (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                            <CheckCircle size={16} color={C.green} />
                            <span style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>{addVehicleFileName}</span>
                          </div>
                        ) : (
                          <div>
                            <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 4 }}>Tap to upload</div>
                            <div style={{ color: C.textMuted, fontSize: 11 }}>PDF only</div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>

                  {/* Error */}
                  {addVehicleError && (
                    <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                      <AlertTriangle size={14} color={C.red} /> {addVehicleError}
                    </div>
                  )}

                  {/* Buttons */}
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      onClick={() => setShowAddVehicle(false)}
                      style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddVehicle}
                      disabled={addVehicleSubmitting}
                      style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: addVehicleSubmitting ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: addVehicleSubmitting ? 0.7 : 1 }}
                    >
                      {addVehicleSubmitting ? "Submitting…" : "Submit"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* GDPR deletion modal */}
        {showGdprModal && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 400 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Request data erasure</div>
              <div style={{ background: C.redBg, border: `1px solid ${C.red}33`, borderRadius: 8, padding: 12, marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <AlertTriangle size={15} color={C.red} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ color: C.red, fontSize: 13, lineHeight: 1.5 }}>
                  This will permanently delete all your compliance data and your portal account. This cannot be undone.
                </div>
              </div>
              <div style={{ color: C.textSub, fontSize: 13, marginBottom: 8 }}>Type <strong style={{ color: C.text }}>DELETE</strong> to confirm.</div>
              <input
                value={gdprConfirmText}
                onChange={e => setGdprConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${gdprConfirmText === "DELETE" ? C.red : C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 16 }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => { setShowGdprModal(false); setGdprConfirmText(""); }}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleGdprDelete}
                  disabled={gdprDeleting || gdprConfirmText !== "DELETE"}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: gdprConfirmText === "DELETE" ? C.red : C.border, color: "#fff", fontSize: 14, fontWeight: 600, cursor: gdprConfirmText === "DELETE" ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: gdprDeleting ? 0.7 : 1 }}
                >
                  {gdprDeleting ? "Deleting…" : "Delete my account"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Insurance detail popup */}
        {showInsuranceDetail && insuranceDetailVehicle && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 400 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Insurance Check Result</div>
                <button onClick={() => setShowInsuranceDetail(false)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", padding: 4 }}>✕</button>
              </div>

              {/* Status badge */}
              <div style={{ marginBottom: 16 }}>
                {(() => {
                  const gf = insuranceDetailVehicle;
                  const insExpired = gf?.policy_expiry && new Date(gf.policy_expiry) < new Date();
                  const color = insExpired ? C.red : gf?.ai_outcome === "Green" ? C.green : gf?.ai_outcome === "Red" ? C.red : C.amber;
                  const label = insExpired ? "Insurance Expired" : gf?.ai_outcome === "Green" ? "Confirmed" : gf?.ai_outcome === "Red" ? "Not Covered" : "Action Required";
                  return (
                    <span style={{ background: `${color}18`, color, border: `1px solid ${color}44`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700 }}>
                      {label}
                    </span>
                  );
                })()}
              </div>

              {/* AI summary */}
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 8 }}>What we found</div>
                <div style={{ color: C.text, fontSize: 14, lineHeight: 1.6 }}>
                  {insuranceDetailVehicle?.ai_summary || "No details available for this check."}
                </div>
              </div>

              {/* Policy expiry */}
              {insuranceDetailVehicle?.policy_expiry && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 4 }}>Policy Expiry</div>
                  <div style={{ color: vesStatus(insuranceDetailVehicle.policy_expiry).color, fontSize: 14, fontWeight: 600 }}>
                    {fmtDate(insuranceDetailVehicle.policy_expiry)}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => { setShowInsuranceDetail(false); setResubmitVehicle(insuranceDetailVehicle); setResubmitFile(null); setResubmitFileName(""); setResubmitError(""); setResubmitDone(false); setShowResubmitInsurance(true); }}
                  style={{ flex: 1, background: C.accent, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  {insuranceDetailVehicle?.ai_outcome === "Green" ? "Update certificate" : "Upload new certificate"}
                </button>
                <button
                  onClick={() => setShowInsuranceDetail(false)}
                  style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.textMuted, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Resubmit insurance modal */}
        {showResubmitInsurance && resubmitVehicle && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 400 }}>
              {resubmitDone ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <CheckCircle size={44} color={C.green} style={{ margin: "0 auto 14px" }} />
                  <div style={{ color: C.green, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Certificate submitted</div>
                  <div style={{ color: C.textMuted, fontSize: 14 }}>Your new insurance certificate has been checked and your record updated.</div>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ color: C.text, fontWeight: 700, fontSize: 18 }}>Update insurance certificate</div>
                    <button onClick={() => setShowResubmitInsurance(false)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", padding: 4 }}>✕</button>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>
                    Uploading a new certificate for <strong style={{ color: C.text }}>{resubmitVehicle.reg}</strong>. Make sure your policy includes business use cover.
                  </div>

                  {/* PDF upload */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 6 }}>New Insurance Certificate (PDF)</div>
                    <label style={{ display: "block", cursor: "pointer" }}>
                      <input type="file" accept="application/pdf" style={{ display: "none" }}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) { setResubmitFile(f); setResubmitFileName(f.name); }
                        }}
                      />
                      <div style={{ border: `2px dashed ${resubmitFile ? C.green : C.border}`, borderRadius: 10, padding: "20px 16px", textAlign: "center", background: resubmitFile ? C.greenBg : C.surfaceAlt, transition: "all 0.2s" }}>
                        {resubmitFile ? (
                          <div style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>✓ {resubmitFileName}</div>
                        ) : (
                          <>
                            <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 4 }}>Tap to select your insurance certificate PDF</div>
                            <div style={{ color: C.textMuted, fontSize: 11 }}>PDF files only</div>
                          </>
                        )}
                      </div>
                    </label>
                  </div>

                  {resubmitError && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{resubmitError}</div>}

                  <button
                    onClick={handleResubmitInsurance}
                    disabled={resubmitSubmitting || !resubmitFile}
                    style={{ width: "100%", background: resubmitSubmitting || !resubmitFile ? C.surfaceAlt : C.accent, border: "none", borderRadius: 8, padding: "12px 0", color: resubmitSubmitting || !resubmitFile ? C.textMuted : "#fff", fontSize: 15, fontWeight: 700, cursor: resubmitSubmitting || !resubmitFile ? "not-allowed" : "pointer", fontFamily: "inherit", transition: "all 0.2s" }}
                  >
                    {resubmitSubmitting ? "Checking certificate…" : "Submit new certificate"}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* GDPR deleted confirmation screen */}
        {gdprDeleted && (
          <div style={{ position: "fixed", inset: 0, background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
            <div style={{ textAlign: "center", maxWidth: 360 }}>
              <CheckCircle size={48} color={C.green} style={{ margin: "0 auto 16px" }} />
              <div style={{ color: C.text, fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Your data has been deleted</div>
              <div style={{ color: C.textMuted, fontSize: 14, lineHeight: 1.6 }}>
                All your compliance data and portal account have been permanently removed in accordance with your GDPR right to erasure.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════════════════════
// APP ENTRY POINT
// ═══════════════════════════════════════════════════════════════════════════════
export default function App() {
  return (
    <DriverPortalApp
      supabaseUrl={SUPABASE_URL}
      supabaseKey={SUPABASE_KEY}
    />
  );
}
